import React, { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import axios from 'axios'
import { usePlan } from '../../../context/PlanContext'
import { toast } from 'react-toastify'
import PhoneInput from 'react-phone-input-2'

interface Props {
  closeWhatsappModal1: () => void
}

const initialValues = {
  phoneNumberId: '',
  phone: '',
  accessToken: ''
}

const validationSchema = Yup.object().shape({
  phoneNumberId: Yup.string().required('Phone Number ID is required'),
  phone: Yup.string().required('Phone Number is required'),
  accessToken: Yup.string().required('Access Token is required')
})

const WhatsappModal: React.FC<Props> = ({ closeWhatsappModal1 }) => {
  const [loadingOL, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [hasExistingConfig, setHasExistingConfig] = useState(false)
  const [verifyToken, setVerifyToken] = useState<string | null>(null)
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null)

  // --- NEW: State for copy-to-clipboard feedback ---
  const [copiedToken, setCopiedToken] = useState(false)
  const [copiedUrl, setCopiedUrl] = useState(false)

  const { currentPlan } = usePlan()

  const fetchExistingConfig = async () => {
    try {
      const token = localStorage.getItem('auth-token')

      const response = await axios.get(
        `${process.env.REACT_APP_RESUME_PARSER_URL}/get-WhatsAppConfig`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )

      if (response.data && response.data.config && response.data.config.length > 0) {
        const configData = response.data.config[0]
        setHasExistingConfig(true)
        setVerifyToken(configData.verifyToken || null)
        setWebhookUrl(configData.webhookUrl || null)
        // Set formik value if config exists
        formik.setFieldValue('phoneNumberId', configData.phoneNumberId || '')
        formik.setFieldValue('phone', configData.testNumber ? `+${configData.testNumber}` : '')
        formik.setFieldValue('accessToken', configData.accessToken || '')
        return configData
      } else {
        setHasExistingConfig(false)
        setVerifyToken(null)
        setWebhookUrl(null)
        return null
      }
    } catch (error) {
      console.error('Error fetching WhatsApp configuration:', error)
      toast.error('Failed to fetch WhatsApp configuration.')
      setHasExistingConfig(false)
      setVerifyToken(null)
      setWebhookUrl(null)
      return null
    }
  }

  // --- NEW: Handler for copying text to clipboard ---
  const handleCopy = (textToCopy: string, type: 'token' | 'url') => {
    if (!textToCopy) return

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        const successMessage = type === 'token' ? 'Verify Token' : 'Callback URL'
        toast.success(`${successMessage} copied to clipboard!`)

        if (type === 'token') {
          setCopiedToken(true)
          setTimeout(() => setCopiedToken(false), 2000) // Reset after 2 seconds
        } else {
          setCopiedUrl(true)
          setTimeout(() => setCopiedUrl(false), 2000) // Reset after 2 seconds
        }
      })
      .catch((err) => {
        console.error('Failed to copy text: ', err)
        toast.error('Failed to copy to clipboard.')
      })
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true)

        const payload = {
          phoneNumberId: values.phoneNumberId,
          testNumber: values.phone?.replace('+', ''),
          accessToken: values.accessToken
        }

        const token = localStorage.getItem('auth-token')

        const endpoint = hasExistingConfig
          ? `${process.env.REACT_APP_RESUME_PARSER_URL}/update-config`
          : `${process.env.REACT_APP_RESUME_PARSER_URL}/save-config`

        await axios.post(endpoint, payload, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })

        toast.success(`Configuration ${hasExistingConfig ? 'updated' : 'saved'} successfully!`)

        // Fetch updated config to get verifyToken & webhookUrl
        await fetchExistingConfig()
      } catch (error) {
        console.error('Error saving configuration:', error)
        toast.error(`Failed to ${hasExistingConfig ? 'update' : 'save'} configuration.`)
      } finally {
        setLoading(false)
      }
    }
  })

  useEffect(() => {
    const loadConfig = async () => {
      setInitialLoading(true)
      await fetchExistingConfig()
      setInitialLoading(false)
    }

    loadConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const renderInput = (
    name: keyof typeof initialValues,
    label: string,
    type = 'text',
    disabled = false
  ) => (
    <>
      <div className='d-flex align-items-start mt-6 px-3 py-2'>
        <label className='fs-6 fw-bold mb-2'>{label}:</label>
      </div>
      <input
        placeholder={`Enter ${label.toLowerCase()}`}
        type={type}
        autoComplete='off'
        {...formik.getFieldProps(name)}
        className={clsx(
          'form-control form-control-lg',
          { 'is-invalid': formik.touched[name] && formik.errors[name] },
          { 'is-valid': formik.touched[name] && !formik.errors[name] }
        )}
        disabled={disabled}
      />
      {formik.touched[name] && formik.errors[name] && (
        <div className='d-flex align-items-start mt-1 px-3 py-2'>
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors[name]}</span>
            </div>
          </div>
        </div>
      )}
    </>
  )

  if (initialLoading) {
    return (
      <div className='card w-600px h-600 mb-4 p-2 position-relative text-center'>
        <button
          type='button'
          className='btn-close position-absolute top-0 end-0 m-n4 rounded-circle'
          aria-label='Close'
          onClick={closeWhatsappModal1}
          style={{
            backgroundColor: 'rgba(218, 222, 243)',
            height: '25px',
            width: '25px',
            color: 'rgba(52, 60, 106)'
          }}
        ></button>
        <div className='d-flex justify-content-center align-items-center h-100'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='card w-600px h-600px mb-4 p-2 position-relative text-center'>
      <button
        type='button'
        className='btn-close position-absolute top-0 end-0 m-n4 rounded-circle'
        aria-label='Close'
        onClick={closeWhatsappModal1}
        style={{
          backgroundColor: 'rgba(218, 222, 243)',
          height: '25px',
          width: '25px',
          color: 'rgba(52, 60, 106)'
        }}
      ></button>

      <div className='d-flex justify-content-between align-items-center px-3 py-2'>
        <h2 className='mb-0'>WhatsApp Configuration</h2>
      </div>
      <div className='d-flex justify-content-between align-items-center px-3 py-2'>
        <p className='mb-0'>
          {hasExistingConfig
            ? 'Update your WhatsApp configuration details below.'
            : 'Enter your Phone Number ID to generate your credentials.'}
        </p>
      </div>

      <div className='px-3 py-2 text-start'>
        <small className='text-muted'>
          <strong>Note:</strong> After you save the Phone Number ID, you must copy the generated{' '}
          <em>Verify Token</em> and <em>Callback URL</em> into your Meta App's webhook
          configuration.
        </small>
      </div>

      <form
        onSubmit={formik.handleSubmit}
        className='d-flex flex-column mb-10 fv-row fv-plugins-icon-container'
      >
        <div className='d-flex align-items-start mt-6 px-3 py-2'>
          <label className='fs-6 fw-bold mb-2'>Test Number:</label>
        </div>
        <PhoneInput
          country={'us'} // default selected country
          value={formik.values.phone}
          onChange={(value: any) => formik.setFieldValue('phone', value)}
          inputClass={clsx(
            'form-control form-control-lg',
            { 'is-invalid': formik.touched.phone && formik.errors.phone },
            { 'is-valid': formik.touched.phone && !formik.errors.phone }
          )}
          inputStyle={{ width: '100%' }}
          buttonClass='dropdown-flag'
          countryCodeEditable={false}
          disabled={hasExistingConfig}
        />

        {formik.touched.phone && formik.errors.phone && (
          <div className='d-flex align-items-start mt-1 px-3 py-2'>
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.phone}</span>
              </div>
            </div>
          </div>
        )}

        {renderInput('phoneNumberId', 'Phone Number ID', 'text', hasExistingConfig)}
        {renderInput('accessToken', 'Access Token')}

        {verifyToken && webhookUrl && (
          <div className='px-3 py-2 mt-4'>
            <div className='p-4 border rounded bg-light'>
              <h5 className='fw-bold mb-4 text-dark text-start'>Your Credentials</h5>

              {/* Verify Token Display */}
              <div className='mb-4'>
                <label className='form-label fw-semibold text-gray-700 d-block text-start'>
                  Verify Token
                </label>
                <div className='d-flex align-items-center justify-content-between p-1 rounded bg-white border'>
                  <code
                    className='fs-6 text-dark me-4'
                    style={{
                      wordBreak: 'break-all',
                      backgroundColor: 'transparent',
                      boxShadow: 'none'
                    }}
                  >
                    {verifyToken}
                  </code>
                  <button
                    type='button'
                    className={`btn btn-sm btn-icon ${
                      copiedToken ? 'btn-light-success' : 'btn-light-primary'
                    }`}
                    onClick={() => handleCopy(verifyToken, 'token')}
                    title={copiedToken ? 'Copied!' : 'Copy to clipboard'}
                  >
                    <i className={`bi ${copiedToken ? 'bi-check-lg' : 'bi-clipboard'}`}></i>
                  </button>
                </div>
              </div>

              {/* Callback URL Display */}
              <div>
                <label className='form-label fw-semibold text-gray-700 d-block text-start'>
                  Callback URL
                </label>
                <div className='d-flex align-items-center justify-content-between p-3 rounded bg-white border'>
                  <code
                    className='fs-6 text-dark me-4 '
                    style={{
                      wordBreak: 'break-all',
                      backgroundColor: 'transparent',
                      boxShadow: 'none'
                    }}
                  >
                    {webhookUrl}
                  </code>
                  <button
                    type='button'
                    className={`btn btn-sm btn-icon ${
                      copiedUrl ? 'btn-light-success' : 'btn-light-primary'
                    }`}
                    onClick={() => handleCopy(webhookUrl, 'url')}
                    title={copiedUrl ? 'Copied!' : 'Copy to clipboard'}
                  >
                    <i className={`bi ${copiedUrl ? 'bi-check-lg' : 'bi-clipboard'}`}></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className='text-start mt-6 px-3'>
          <button
            type='submit'
            className='btn btn-lg btn-primary w-100 mb-5'
            disabled={formik.isSubmitting || !formik.isValid || loadingOL}
          >
            {!loadingOL && (
              <span className='indicator-label'>{hasExistingConfig ? 'Update' : 'Submit'}</span>
            )}
            {loadingOL && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default WhatsappModal
